import Layout from '@/components/feature/Layout';
import FaqAccordion from '@/components/feature/FaqAccordion';
import { PageBreadcrumb, RelatedLinks, ctaSidebar } from '@/pages/shared/PageHelpers';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'Como Soldar Geomembrana: Guia Passo a Passo',
  description:
    'Guia prático passo a passo para soldar geomembrana PEAD corretamente, desde a preparação do local até a inspeção final e testes de estanqueidade.',
  url: 'https://maquinadesoldageomembrana.com.br/como-soldar-geomembrana',
  inLanguage: 'pt-BR',
  step: [
    { '@type': 'HowToStep', position: 1, name: 'Preparação do local', text: 'Nivele e compacte o terreno, remova pedras e objetos pontiagudos.' },
    { '@type': 'HowToStep', position: 2, name: 'Limpeza das folhas', text: 'Limpe e seque a área de sobreposição das geomembranas.' },
    { '@type': 'HowToStep', position: 3, name: 'Sobreposição', text: 'Posicione as folhas com sobreposição de 7 a 15 cm.' },
    { '@type': 'HowToStep', position: 4, name: 'Ajuste de parâmetros', text: 'Configure temperatura, velocidade e pressão do equipamento.' },
    { '@type': 'HowToStep', position: 5, name: 'Solda de teste', text: 'Realize uma solda de teste e valide por tração antes de produzir.' },
    { '@type': 'HowToStep', position: 6, name: 'Soldagem contínua', text: 'Execute a solda mantendo parâmetros constantes ao longo da emenda.' },
    { '@type': 'HowToStep', position: 7, name: 'Inspeção final', text: 'Realize inspeção visual e testes de pressão de ar ou vácuo.' },
  ],
};

const faq = [
  {
    question: 'É preciso um profissional para soldar geomembrana?',
    answer:
      'Sim. A soldagem de geomembrana exige operador treinado e equipamento adequado. Parâmetros incorretos de temperatura, velocidade ou pressão podem comprometer a estanqueidade de todo o sistema de impermeabilização.',
  },
  {
    question: 'Qual a sobreposição ideal entre as folhas?',
    answer:
      'A sobreposição ideal varia entre 7 e 15 cm, conforme a especificação do projeto e a norma aplicável. Uma sobreposição insuficiente reduz a área de fusão e enfraquece a emenda.',
  },
  {
    question: 'Posso soldar geomembrana em dias de chuva?',
    answer:
      'Não. A superfície de soldagem deve estar completamente seca e limpa. A umidade impede a fusão correta dos polímeros e pode gerar emendas frágeis com risco de vazamento.',
  },
  {
    question: 'Como saber se a solda ficou boa?',
    answer:
      'A qualidade da solda é validada por teste de tração (peel e shear) na solda de teste e, na produção, por inspeção visual contínua, teste de pressão de ar no canal da solda dupla e teste de vácuo nas extrusões.',
  },
];

const steps = [
  { icon: 'ri-road-map-line', t: '1. Preparação do Local', d: 'Nivele e compacte o terreno. Remova pedras, galhos e objetos pontiagudos que possam danificar a manta. A superfície de apoio deve estar firme e regular.' },
  { icon: 'ri-brush-line', t: '2. Limpeza das Folhas', d: 'Limpe e seque a área de sobreposição. Remova poeira, areia e umidade com pano limpo ou solvente compatível para garantir a fusão dos polímeros.' },
  { icon: 'ri-ruler-line', t: '3. Sobreposição', d: 'Posicione as folhas com sobreposição de 7 a 15 cm, conforme o projeto. Fixe temporariamente para evitar deslocamento durante a solda.' },
  { icon: 'ri-temp-hot-line', t: '4. Ajuste de Parâmetros', d: 'Configure temperatura (280–380°C para PEAD), velocidade e pressão dos rolos conforme a espessura da geomembrana.' },
  { icon: 'ri-test-tube-line', t: '5. Solda de Teste', d: 'Realize uma solda de teste em retalho do mesmo material e valide por tração antes de iniciar a produção. Repita ao longo do dia.' },
  { icon: 'ri-fire-line', t: '6. Soldagem Contínua', d: 'Execute a solda mantendo parâmetros constantes. Acompanhe o alinhamento e evite dobras ou bolhas na emenda.' },
  { icon: 'ri-eye-line', t: '7. Inspeção Visual', d: 'Verifique a continuidade e o aspecto da solda. Qualquer irregularidade deve ser marcada e reparada.' },
  { icon: 'ri-shield-check-line', t: '8. Teste de Estanqueidade', d: 'Aplique teste de pressão de ar no canal da solda dupla e teste de vácuo nas extrusões. Registre os resultados.' },
];

export default function ComoSoldarGeomembranaPage() {
  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      <div className="w-full px-4 md:px-6 lg:px-10 pt-28 md:pt-32 pb-16 md:pb-24">
        <PageBreadcrumb items={[{ label: 'Como Soldar Geomembrana' }]} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-12">
          <div className="lg:col-span-2">
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 uppercase leading-tight mb-6">
              Como Soldar Geomembrana: Guia Passo a Passo
            </h1>

            <p className="text-foreground-600 leading-relaxed mb-8">
              Aprenda o processo completo de <strong className="text-foreground-950">soldagem de geomembrana</strong>, etapa a etapa, para garantir emendas estanques e duráveis em qualquer projeto de impermeabilização.
            </p>

            <div className="space-y-4">
              {steps.map((s) => (
                <div key={s.t} className="flex items-start gap-4 bg-white rounded-2xl border border-background-200/70 p-5">
                  <div className="w-11 h-11 flex items-center justify-center bg-primary-100 rounded-xl flex-shrink-0">
                    <i className={`${s.icon} text-primary-600 text-lg`} />
                  </div>
                  <div>
                    <h2 className="font-heading text-base font-bold text-foreground-950 uppercase mb-1">{s.t}</h2>
                    <p className="text-foreground-600 text-sm leading-relaxed">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 bg-background-100 rounded-2xl border border-background-200/70 p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950 uppercase mb-2 flex items-center gap-2">
                <i className="ri-lightbulb-line text-accent-500" /> Dica Importante
              </h2>
              <p className="text-foreground-600 text-sm leading-relaxed">
                Mantenha sempre um registro dos parâmetros de solda (temperatura, velocidade, pressão) e dos testes realizados. Esse histórico é fundamental para a rastreabilidade e o controle de qualidade da obra, especialmente em projetos que exigem conformidade com a norma NBR 16199.
              </p>
            </div>

            <div className="mt-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mb-5">Perguntas Frequentes</h2>
              <FaqAccordion items={faq} />
            </div>

            <RelatedLinks
              links={[
                { to: '/maquina-de-solda-de-geomembrana', label: 'Máquina de Solda de Geomembrana' },
                { to: '/soldagem-de-geomembrana', label: 'Técnicas de Soldagem' },
                { to: '/cunha-quente-geomembrana', label: 'Cunha Quente' },
                { to: '/geomembrana-pead', label: 'Geomembrana PEAD' },
                { to: '/comprar-maquina-de-solda-de-geomembrana', label: 'Comprar Máquina' },
              ]}
            />
          </div>

          <div className="space-y-6">{ctaSidebar}</div>
        </div>
      </div>
    </Layout>
  );
}